/**
 * The "Your next milestone" lines on a rep's or manager's Today (sales comp change, 27 October 2026). The session override step is named
 * first: an SP Manager's partner session points on their own active partners, a Sales Manager's team session points on the team's active
 * partners. Then the partner hardware margin step. The Api reads both steps from the person's accepted plan (salesNextSessionStep in
 * @evi/shared); this only words them. A stamp is set at launch, so a higher step applies to chargers launched after it is reached.
 */
export interface SalesTodayMilestones {
  plan_revision: number | null;
  milestone: { partners_needed: number; rate_bps: number } | null;
  /** Absent from an Api older than 28 October 2026, which then says nothing about session points. */
  session_milestone?: { kind: 'partner_session_points' | 'team_session_points'; at: number; needed: number; rate_bps: number } | null;
}
const percent = (bps: number) => `${Number((bps / 100).toFixed(2))}%`;
const plural = (n: number, word: string) => `${n} more ${word}${n === 1 ? '' : 's'}`;

export function salesTodayMilestoneLines(t: SalesTodayMilestones): string[] {
  if (t.plan_revision == null) return ['Accept a plan to see its milestones.'];
  const s = t.session_milestone;
  if (s === null && !t.milestone) return ['Your accepted plan has no higher rate to reach.'];
  const lines: string[] = [];
  if (s) lines.push(s.kind === 'team_session_points'
    ? `${plural(s.needed, 'team active partner')} (${s.at} in all) raises your share of each paid session on your team's newly launched chargers to ${percent(s.rate_bps)}.`
    : `${plural(s.needed, 'active partner')} (${s.at} in all) raises your share of each paid session on your partners' newly launched chargers to ${percent(s.rate_bps)}.`);
  else if (s === null) lines.push('Your accepted plan has no higher session share to reach.');
  lines.push(t.milestone
    ? `${plural(t.milestone.partners_needed, 'active partner')} raises your rate on your partners' hardware orders to ${percent(t.milestone.rate_bps)}.`
    : 'Your accepted plan has no higher hardware rate to reach.');
  return lines;
}
